import React, { useEffect, useRef } from 'react';
import styles from './Checkbox.module.css';
import UncheckedIcon from '../../Icons/UncheckedIcon';
import CheckedIcon from '../../Icons/CheckedIcon';
import IndeterminateIcon from '../../Icons/IndeterminateIcon';

/**
 * A premium, highly customizable custom Checkbox component with sleek SVG indicators.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.checked=false] - Controls the active checked state of the checkbox.
 * @param {boolean} [props.indeterminate=false] - Renders the mixed/partial-selection state. Takes precedence over `checked` visually, but does not change the underlying `checked` value.
 * @param {(value?: any) => void} [props.onChange] - Callback fired when the check state is toggled.
 * @param {boolean} [props.disabled=false] - If true, disables interaction and styles the checkbox as inactive.
 * @param {any} [props.value] - Optional value associated with the checkbox element.
 * @param {string} [props.color] - Custom theme/primary hex color mapping for the active checked/indeterminate state.
 * @param {string} [props.className] - External CSS class name to style the checkbox container.
 * @param {'small' | 'medium'} [props.size] - Optional sizing variant.
 * @param {import('react').CSSProperties} [props.style] - Inline style for the checkbox container.
 * @param {import('react').ReactNode} [props.icon] - Custom element rendered in place of the default unchecked icon.
 * @param {import('react').ReactNode} [props.checkedIcon] - Custom element rendered in place of the default checked icon.
 * @param {import('react').ReactNode} [props.indeterminateIcon] - Custom element rendered in place of the default indeterminate icon.
 * @param {import('react').ReactNode} [props.children] - Optional label content rendered after the icon, inside the clickable area.
 */
const Checkbox = ({
  onChange,
  className,
  disabled = false,
  value,
  checked,
  indeterminate = false,
  color,
  size,
  style,
  icon,
  checkedIcon,
  indeterminateIcon,
  children,
  ...rest
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    // `indeterminate` is a DOM-only property, not an HTML attribute, so it can't be set via JSX.
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = () => {
    if (disabled) return;
    onChange?.(value);
  };

  const active = checked || indeterminate;

  const wrapperClass = [
    styles.wrapper,
    size && styles[size],
    active && styles.checked,
    disabled && styles.disabled,
    className,
  ].filter(Boolean).join(' ');

  const colorStyle = (active && color) ? { color } : undefined;
  const wrapperStyle = { ...colorStyle, ...style };

  const customIcon = indeterminate ? indeterminateIcon : checked ? checkedIcon : icon;
  const DefaultIcon = indeterminate ? IndeterminateIcon : checked ? CheckedIcon : UncheckedIcon;

  return (
    <label className={wrapperClass} style={wrapperStyle}>
      <input
        ref={inputRef}
        type="checkbox"
        className={styles.hiddenInput}
        checked={checked}
        onChange={handleChange}
        value={value}
        disabled={disabled}
        {...rest}
      />
      {customIcon !== undefined ? customIcon : <DefaultIcon />}
      {children}
    </label>
  );
};

export default Checkbox;
